import React, { useState, useEffect } from 'react';
import { ReceiptText, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const CAT_COLORS = {
  boost: 'bg-orange-100 text-orange-700', feature: 'bg-purple-100 text-purple-700', urgent: 'bg-red-100 text-red-700',
  card_processing_fee: 'bg-blue-100 text-blue-700', payout_fee: 'bg-gray-100 text-gray-600',
  pc_membership: 'bg-emerald-100 text-emerald-700', pc_seller_pass: 'bg-emerald-100 text-emerald-700',
  pc_listing: 'bg-emerald-100 text-emerald-700', bank_transfer: 'bg-amber-100 text-amber-700',
};

export const RevenueLedger = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/finance/ledger`);
      if (res.ok) setData(await res.json());
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="revenue-ledger">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ReceiptText className="w-4 h-4 text-gray-400" />
          <h4 className="text-sm font-semibold text-gray-900">Itemised Fees — who paid what</h4>
          {data?.summary && <span className="text-xs text-gray-400">({data.summary.entry_count} entries · ${data.summary.total_credits} in)</span>}
        </div>
        <Button size="sm" variant="ghost" onClick={load}><RefreshCw className="w-3 h-3" /></Button>
      </div>
      {loading ? (
        <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-300" /></div>
      ) : !data?.ledger?.length ? (
        <p className="text-sm text-gray-400 text-center py-6">No fees recorded yet.</p>
      ) : (
        <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white"><tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b">
              <th className="px-4 py-2">When</th><th className="px-4 py-2">Who</th><th className="px-4 py-2">What</th><th className="px-4 py-2 text-right">Amount</th>
            </tr></thead>
            <tbody>
              {data.ledger.slice(0, 150).map((e, i) => (
                <tr key={e.reference_id || i} className="border-b last:border-0 hover:bg-gray-50" data-testid={`ledger-row-${i}`}>
                  <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{e.date ? new Date(e.date).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                  <td className="px-4 py-2.5">
                    <p className="text-gray-900 text-xs font-medium">{e.who || '—'}</p>
                    {e.who_email && <p className="text-[10px] text-gray-400">{e.who_email}</p>}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full mr-1.5 ${CAT_COLORS[e.category] || 'bg-gray-100 text-gray-600'}`}>{(e.category || '').replace(/_/g, ' ')}</span>
                    <span className="text-xs text-gray-600">{e.description}</span>
                  </td>
                  <td className={`px-4 py-2.5 text-right font-semibold text-xs whitespace-nowrap ${e.debit ? 'text-red-600' : 'text-emerald-700'}`}>
                    {e.debit ? `−$${Number(e.debit).toFixed(2)}` : `+$${Number(e.credit).toFixed(2)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RevenueLedger;
